import {Container, Flex, Grid, Text} from '@chakra-ui/react';

import {useTranslate} from '@eksab/i18n';
import {useInfiniteScroll} from '@eksab/hooks';
import {InfiniteScrollingIndicator} from '@eksab/components';

import type {CompetitionConfig} from '@eksab/features/play/types';

import {Header} from './Header';
import {CompetitionCard} from '../competition-card';
import {Filters, useFiltersState} from '../filters/Filters';
import {useCompetitionsQuery} from '../../hooks/useCompetitionsQuery';

type CompetitionsProps = {
  game: CompetitionConfig;
};

export function Competitions({game}: CompetitionsProps) {
  const t = useTranslate();
  const [filters, setFilters, count] = useFiltersState(game.name);
  const {data, isLoading, isError, isFetched, fetchNextPage, isFetchingNextPage, hasNextPage} =
    useCompetitionsQuery(filters);
  useInfiniteScroll({
    action: fetchNextPage,
    condition: !isFetchingNextPage && !!hasNextPage,
    threshold: 0.3,
  });
  const competitions = data?.pages?.flatMap((page) => page.data);
  const hasCompetitions = !!competitions?.length;

  return (
    <>
      <Header game={game} />

      <Flex as="section" align="center" justify="space-between" gap="4">
        <Text as="h2" color="#191b20" fontSize={{base: 'md', md: 'xl', lg: '2xl'}} fontWeight="700">
          {t('competitions.listing.title')}
        </Text>

        <Filters count={count} game={game.name} initialState={filters} onSubmit={setFilters} />
      </Flex>

      {competitions?.length || isLoading ? (
        <Grid as="section" templateColumns={['auto', null, 'repeat(2, 1fr)', null, 'repeat(3, 1fr)']} gap="5">
          {isLoading
            ? Array.from({length: 9}).map((_, idx) => <CompetitionCard.Skeleton key={idx} />)
            : competitions?.map((competition) => <CompetitionCard key={competition.id} competition={competition} />)}
        </Grid>
      ) : (
        <Container h="50vh" alignItems="center" justifyContent="center" textAlign="center">
          {isError ? (
            <Text>{t('app.errors.try-again')}</Text>
          ) : (
            <Text lineHeight="1.9">
              {t('competitions.no-competitions', {
                filter: t(`competitions.filters.upcoming`),
                br: (chunks) => (
                  <>
                    <br />
                    {chunks}
                  </>
                ),
              })}
            </Text>
          )}
        </Container>
      )}

      {isFetched && hasCompetitions && <InfiniteScrollingIndicator isOver={!hasNextPage} mt={[6, 12]} />}
    </>
  );
}
